/**
 * 화면이 들고 있는 밸브·시나리오 상태.
 *
 * 입력은 전부 문자열로 보관하고 계산 직전에만 숫자로 바꾼다.
 * (입력 도중의 "0.", "-", "" 같은 중간 상태를 그대로 두려는 것)
 */
import type {
  ContingencyKind,
  FluidSpec,
  InstallationKind,
  PsvCase,
  ScenarioEntry,
} from "../calc/governingCase";
import { defaultContingency, findScenario } from "../data/scenarios";
import { num } from "./formBits";
import {
  convertRateCalc,
  kindsForItem,
  newRateCalc,
  type RateCalcState,
} from "./rateCalc";
import {
  ATMOSPHERIC_KPA,
  convertInput,
  formatFromKpa,
  toKpa,
  type PressureUnit,
} from "./pressureUnit";

/** 케이스 검토 화면이 지원하는 유체 상태. */
export type SimpleFluidKind = "gas" | "steam" | "liquid";

export const FLUID_LABELS: Record<SimpleFluidKind, string> = {
  gas: "기체·증기",
  steam: "수증기(포화)",
  liquid: "액체",
};

export interface ScenarioRow {
  readonly id: string;
  item: number;
  /** 설계자가 직접 쓰는 과압 상황 설명 */
  description: string;
  contingency: ContingencyKind;
  included: boolean;
  fluidKind: SimpleFluidKind;
  /** 기체·수증기는 kg/h, 액체는 L/min */
  flow: string;
  temperature: string;
  molecularWeight: string;
  specificHeatRatio: string;
  compressibility: string;
  specificGravity: string;
  backpressure: string;
  /**
   * 소요 분출용량 계산기의 입력. 13·14·15번 시나리오에서만 쓰이지만
   * 모든 행이 들고 있다 — 시나리오 번호를 바꿔도 넣어둔 값이 사라지지 않게.
   */
  rateCalc: RateCalcState;
}

export interface CaseState {
  readonly id: string;
  tag: string;
  /** 보호 대상 설비 이름 */
  equipment: string;
  setPressure: string;
  installation: InstallationKind;
  rows: ScenarioRow[];
}

let seq = 0;

/**
 * 화면 안에서만 쓰는 식별자. 저장 파일에도 실리지만, 불러올 때는
 * 전부 새로 발급한다(기존 화면의 id 와 충돌하지 않게 하려는 것).
 */
export const nextId = (prefix: string) => `${prefix}-${(seq += 1)}`;

export function newScenarioRow(
  item = 1,
  fluidKind: SimpleFluidKind = "gas",
): ScenarioRow {
  return {
    id: nextId("row"),
    item,
    description: "",
    contingency: defaultContingency(item),
    included: true,
    fluidKind,
    flow: "20000",
    temperature: "348",
    molecularWeight: "51",
    specificHeatRatio: "1.11",
    compressibility: "0.9",
    specificGravity: "0.9",
    backpressure: "0",
    rateCalc: newRateCalc(item),
  };
}

/**
 * 시나리오 번호를 바꿨을 때 계산기 종류를 그 번호에 맞게 되돌린다.
 * 이미 그 번호에서 고를 수 있는 종류면 그대로 둔다.
 */
export function syncRateCalcKind(
  rateCalc: RateCalcState,
  item: number,
): RateCalcState {
  const kinds = kindsForItem(item);
  if (kinds.length === 0 || kinds.includes(rateCalc.kind)) return rateCalc;
  return { ...rateCalc, kind: kinds[0]! };
}

export function newCase(tag: string): CaseState {
  return {
    id: nextId("case"),
    tag,
    equipment: "",
    setPressure: "517",
    installation: "single",
    rows: [newScenarioRow(1)],
  };
}

/** 초기 화면에 띄울 예시. 유량↔면적 역전을 보여주는 조합이다. */
export function initialCases(): CaseState[] {
  const c = newCase("PSV-101");
  c.equipment = "탈에탄기 환류드럼";
  c.rows = [
    {
      ...newScenarioRow(1),
      description: "하류 차단밸브 오조작으로 배출 경로 폐쇄",
      flow: "24270",
    },
    {
      ...newScenarioRow(14),
      description: "드럼 주변 풀파이어, 습윤면적 기준 증발량",
      flow: "26000",
    },
  ];
  return [c];
}

function toFluidSpec(r: ScenarioRow, unit: PressureUnit): FluidSpec {
  // 화면 입력은 사용자가 고른 단위의 게이지압. 계산 계층은 kPa 만 받는다.
  const backpressureGaugeKpa = toKpa(num(r.backpressure), unit);
  const backpressureAbsKpa = backpressureGaugeKpa + ATMOSPHERIC_KPA;

  switch (r.fluidKind) {
    case "gas":
      return {
        kind: "gas",
        requiredFlowKgH: num(r.flow),
        relievingTemperatureK: num(r.temperature),
        compressibility: num(r.compressibility),
        molecularWeight: num(r.molecularWeight),
        specificHeatRatio: num(r.specificHeatRatio),
        backpressureKpaAbs: backpressureAbsKpa,
        valveType: "conventional",
      };
    case "steam":
      return {
        kind: "steam",
        requiredFlowKgH: num(r.flow),
        saturated: true,
        valveType: "conventional",
      };
    case "liquid":
      return {
        kind: "liquid",
        certification: "certified",
        requiredFlowLpm: num(r.flow),
        specificGravity: num(r.specificGravity),
        backpressureKpaGauge: backpressureGaugeKpa,
        valveType: "conventional",
      };
  }
}

/**
 * 화면 상태를 계산 계층이 쓰는 형태로 바꾼다.
 * **여기가 단위 환산이 일어나는 유일한 지점이다** — 계산 계층은 kPa 만 안다.
 */
export function toPsvCase(c: CaseState, unit: PressureUnit): PsvCase {
  return {
    tag: c.tag,
    setPressureKpaGauge: toKpa(num(c.setPressure), unit),
    installation: c.installation,
    scenarios: c.rows.map<ScenarioEntry>((r) => ({
      id: r.id,
      item: r.item,
      label: findScenario(r.item)?.conditionKo ?? `항목 ${r.item}`,
      description: r.description,
      contingency: r.contingency,
      included: r.included,
      fluid: toFluidSpec(r, unit),
    })),
  };
}

/**
 * 밸브 하나를 지우고, 그 다음에 선택할 밸브를 정한다.
 *
 * **삭제 후에는 마지막에 등록된 밸브를 고른다.**
 * 첫 번째로 되돌리면 '밸브 삭제'를 연달아 눌렀을 때 지우려던 최근 밸브가
 * 아니라 **가장 먼저 등록한 밸브부터 지워진다.** 실제로 그 버그가 있었다.
 *
 * 마지막 한 개는 지우지 않는다(밸브가 0개인 화면은 쓸모가 없다).
 * 지울 수 없으면 null 을 돌려준다.
 */
export function removeCaseFrom(
  cases: readonly CaseState[],
  removedId: string,
  activeId: string,
): { readonly cases: CaseState[]; readonly activeId: string } | null {
  if (cases.length <= 1) return null;
  if (!cases.some((c) => c.id === removedId)) return null;

  const next = cases.filter((c) => c.id !== removedId);
  const activeSurvives = next.some((c) => c.id === activeId);
  return {
    cases: next,
    activeId: activeSurvives ? activeId : next[next.length - 1]!.id,
  };
}

/**
 * 단위를 바꿀 때 저장된 입력 문자열을 새 단위 값으로 환산한다.
 *
 * 숫자를 그대로 두고 표기만 바꾸면 **물리량이 조용히 달라진다.**
 * (517 kPag 를 barg 로 바꾸면 517 barg = 51,700 kPa)
 */
export function convertCasePressures(
  cases: readonly CaseState[],
  from: PressureUnit,
  to: PressureUnit,
): CaseState[] {
  if (from === to) return [...cases];
  return cases.map((c) => convertCase(c, from, to));
}

function convertCase(
  c: CaseState,
  from: PressureUnit,
  to: PressureUnit,
): CaseState {
  if (from === to) return c;
  return {
    ...c,
    setPressure: convertInput(c.setPressure, from, to),
    rows: c.rows.map((r) => convertRow(r, from, to)),
  };
}

function convertRow(
  r: ScenarioRow,
  from: PressureUnit,
  to: PressureUnit,
): ScenarioRow {
  if (from === to) return r;
  return {
    ...r,
    backpressure: convertInput(r.backpressure, from, to),
    // 계산기 안의 압력 입력도 같이 환산해야 물리량이 보존된다.
    rateCalc: convertRateCalc(r.rateCalc, from, to),
  };
}

/*
 * ── 기본값과 단위 ────────────────────────────────────────
 *
 * 위의 기본값들은 전부 **kPa 기준**이다. 사용자가 bar 로 바꿔 둔 상태에서
 * 밸브나 시나리오를 새로 추가하면, 그 숫자가 그대로 bar 로 읽혀
 * **100 배 큰 압력**이 들어간다. 실제로 화재 계산기의 운전 압력 기본값
 * 700 이 700 barg 로 읽혀 배출 온도가 3 K 로 나오는 것을 확인했다.
 *
 * 그래서 새로 만드는 것은 항상 kPa 로 만든 뒤 현재 단위로 환산해 준다.
 */

/** 현재 단위에 맞춘 새 시나리오 행. */
export function newScenarioRowIn(
  unit: PressureUnit,
  item = 1,
  fluidKind: SimpleFluidKind = "gas",
): ScenarioRow {
  return convertRow(newScenarioRow(item, fluidKind), "kPa", unit);
}

/** 현재 단위에 맞춘 새 밸브. */
export function newCaseIn(unit: PressureUnit, tag: string): CaseState {
  return convertCase(newCase(tag), "kPa", unit);
}

/** 현재 단위에 맞춘 초기 예시. */
export function initialCasesIn(unit: PressureUnit): CaseState[] {
  return initialCases().map((c) => convertCase(c, "kPa", unit));
}

/**
 * 플레어 헤더에서 구한 배압을 밸브들에 넣는다.
 *
 * **태그로 짝을 짓는다.** 어느 과압 시나리오가 어느 동시 분출 조합에
 * 대응하는지는 설계자만 알 수 있으므로, 짝이 맞는 밸브의 **모든 시나리오
 * 행**에 같은 값을 넣는다. 가장 보수적인 선택이고, 화면에서 그 사실을 밝힌다.
 *
 * 태그는 앞뒤 공백을 무시하고 비교한다 — 손으로 옮겨 적다 보면 흔히 섞인다.
 */
export function applyBackpressureByTag(
  cases: readonly CaseState[],
  entries: ReadonlyArray<{ tag: string; backpressureKpaGauge: number }>,
  unit: PressureUnit,
): { readonly cases: CaseState[]; readonly appliedTags: string[] } {
  const byTag = new Map(
    entries.map((e) => [e.tag.trim(), e.backpressureKpaGauge]),
  );
  const appliedTags: string[] = [];

  const next = cases.map((c) => {
    const kpa = byTag.get(c.tag.trim());
    if (kpa === undefined) return c;
    appliedTags.push(c.tag.trim());
    const text = formatFromKpa(kpa, unit);
    return { ...c, rows: c.rows.map((r) => ({ ...r, backpressure: text })) };
  });

  return { cases: next, appliedTags };
}
